import { ColDef } from "@ag-grid-community/core";
import { inject, Injectable } from "@angular/core";
import { AlgoMode, CampaignType, StrategyStateEnum } from "@front/m19-api-client";
import { StrategyEx } from "@front/m19-models";
import {
  AccountSelectionService,
  BaseStrategyService,
  SbStrategyService,
  SdStrategyService,
  SpStrategyService,
  StrategyCache,
} from "@front/m19-services";
import {
  BulkUploadPreviewStatus,
  BulkUploadRunStatus,
} from "@m19-board/bulk-upload/bulk-upload-grid-cells/bulk-upload-type-icon.component";
import { BulkCsvField, BulkImportService } from "@m19-board/strategies/strategy-bulk-upload-modal/bulk-import.service";
import {
  BehaviorSubject,
  catchError,
  forkJoin,
  isObservable,
  map,
  Observable,
  of,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from "rxjs";
import {
  BulkUploadBuckets,
  BulkUploadFieldPreview,
  BulkUploadProgressCounter,
  BulkUploadRowPreview,
  BulkUploadRunResultItem,
  BulkUploadHandler,
  rowHasFieldError,
  rowIsUnchanged,
} from "../bulk-upload-context.service";
import { BulkUploadGridUtilsService } from "../bulk-upload-grid-utils.service";

export interface PromoDaysBoost {
  strategyId: number;
  name: string;
  activatePrimeDayBoost: boolean;
  primeDayBoost: number | undefined;
}

@Injectable()
export class PromoBoostBulkUploadHandler implements BulkUploadHandler<PromoDaysBoost> {
  private readonly bulkImportService = inject(BulkImportService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly strategyCache = inject(StrategyCache);
  private readonly bulkUploadGridUtilsService = inject(BulkUploadGridUtilsService);
  private readonly spStrategyService = inject(SpStrategyService);
  private readonly sdStrategyService = inject(SdStrategyService);
  private readonly sbStrategyService = inject(SbStrategyService);

  protected readonly seenStrategyIdsDuringPreview = new Set<number>();

  private readonly bulkConfig = this.bulkImportService.PromoDaysBoostBulkConfig as unknown as BulkCsvField<
    PromoDaysBoost,
    keyof PromoDaysBoost,
    PromoDaysBoost[keyof PromoDaysBoost]
  >[];

  public readonly expectedHeaders: string[] = this.bulkConfig.map((field) => field.csvField.header);

  private readonly progress = new BehaviorSubject<BulkUploadProgressCounter>({ completed: 0, total: 0 });
  public readonly progress$ = this.progress.asObservable();

  protected readonly strategyIndex$ = this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
    switchMap((am) => this.strategyCache.getAllStrategyIndex(am.accountId, am.marketplace)),
    map((index) => {
      const filtered = new Map<number, StrategyEx>();
      for (const [id, s] of index) {
        if (s.state !== StrategyStateEnum.PAUSED && !s.isTacos() && s.campaignType !== CampaignType.DSP) {
          filtered.set(id, s);
        }
      }
      return filtered;
    }),
  );

  buildPreview(rows: string[]): Observable<BulkUploadBuckets<PromoDaysBoost>> {
    this.seenStrategyIdsDuringPreview.clear();
    if (rows.length === 0) return of([]);
    return forkJoin(rows.map((row) => this.parseRowAsPromoDaysBoost(row.split(/\t/)))).pipe(
      map((parsed) => parsed.filter((row) => row !== null)),
    );
  }

  run(buckets: BulkUploadBuckets<PromoDaysBoost>): Observable<BulkUploadRunResultItem<PromoDaysBoost>[]> {
    const updates = buckets.filter((b) => b.status === BulkUploadPreviewStatus.UPDATE);
    this.progress.next({ completed: 0, total: updates.length });

    if (updates.length === 0) return of([]);

    const requests = updates.map((row) =>
      this.strategyIndex$.pipe(
        take(1),
        withLatestFrom(this.accountSelectionService.singleAccountMarketplaceSelection$),
        switchMap(([strategyIndex, am]) => {
          const existing = strategyIndex.get(row.parsedEntity.strategyId!);
          if (!existing) {
            return of({
              parsedEntity: row.parsedEntity,
              resultEntity: null,
              error: "Strategy does not exist",
              status: BulkUploadRunStatus.FAILED,
            } satisfies BulkUploadRunResultItem<PromoDaysBoost>);
          }

          return this.getStrategyService(existing.campaignType)
            .updateStrategyPrimeDayBoost(
              am.accountId,
              am.marketplace,
              am.resourceOrganizationId!,
              row.parsedEntity.strategyId!,
              row.parsedEntity.primeDayBoost,
            )
            .pipe(
              map(
                (result): BulkUploadRunResultItem<PromoDaysBoost> => ({
                  parsedEntity: row.parsedEntity,
                  resultEntity: {
                    strategyId: result.strategyId,
                    name: result.name,
                    activatePrimeDayBoost: result.primeDayBoost != null,
                    primeDayBoost: result.primeDayBoost,
                  },
                  status: BulkUploadRunStatus.UPDATED,
                }),
              ),
              catchError((err) =>
                of({
                  parsedEntity: row.parsedEntity,
                  resultEntity: null,
                  error: typeof err === "string" ? err : (err?.message ?? "error"),
                  status: BulkUploadRunStatus.FAILED,
                } satisfies BulkUploadRunResultItem<PromoDaysBoost>),
              ),
            );
        }),
        tap(() => {
          const cur = this.progress.value;
          this.progress.next({ ...cur, completed: cur.completed + 1 });
        }),
      ),
    );

    return forkJoin(requests);
  }

  getPreviewGridColumnsDefs(): ColDef<BulkUploadRowPreview<PromoDaysBoost>>[] {
    return this.bulkUploadGridUtilsService.getPreviewGridColumnsDefs(this.bulkConfig, "name");
  }

  getRunResultGridColumnsDefs(): ColDef<BulkUploadRunResultItem<PromoDaysBoost>>[] {
    const withoutName = this.bulkConfig.filter((field) => field.field !== "name");
    return this.bulkUploadGridUtilsService.getRunResultGridColumnsDefs(withoutName, "name", "strategyId");
  }

  export(): void {
    this.bulkImportService.exportPromoDaysBoostCsv();
  }

  private parseRowAsPromoDaysBoost(fields: string[]): Observable<BulkUploadRowPreview<PromoDaysBoost> | null> {
    const parsed = this.bulkImportService.parseStrategyBoostCsvRow(fields);
    if (!parsed) {
      return of({
        parsedEntity: {},
        fieldsPreview: this.bulkConfig.map((field, i) => ({
          field: field.field,
          providedValue: fields[i] ?? "",
          parsedValue: undefined,
          error: i === 0 ? "Wrong line format" : undefined,
          isCreation: false,
          hasChanged: false,
        })),
        status: BulkUploadPreviewStatus.VALUE_ERROR,
      });
    }

    const activatePrimeDayBoost = Boolean(parsed.activatePrimeDayBoost);
    const partial: Partial<PromoDaysBoost> = {
      strategyId: parsed.strategyId,
      name: parsed.name,
      activatePrimeDayBoost,
      primeDayBoost: activatePrimeDayBoost ? parsed.primeDayBoost : undefined,
    };

    return forkJoin(
      this.bulkConfig.map((field) => {
        const error = promoDaysBoostFieldValidators[field.field]?.bind(this)(partial);
        return isObservable(error) ? error : of(error);
      }),
    ).pipe(
      withLatestFrom(this.strategyIndex$.pipe(take(1))),
      map(([errors, strategyIndex]) => {
        const existing = partial.strategyId !== undefined ? strategyIndex.get(partial.strategyId) : undefined;
        const parsedEntity: Partial<PromoDaysBoost> = {
          ...partial,
          name: existing?.name ?? partial.name,
        };
        const fieldsPreview: BulkUploadFieldPreview<PromoDaysBoost>[] = errors.map((error, i) => {
          const field = this.bulkConfig[i].field;
          const existingValue = this.getExistingFieldValue(field, existing);
          const parsedValue = parsedEntity[field];
          return {
            field,
            providedValue: fields[i] ?? "",
            parsedValue: parsedValue,
            error: error ?? undefined,
            isCreation: false,
            existingValue,
            hasChanged: existing ? this.hasFieldChanged(field, existingValue, parsedValue) : false,
          };
        });
        return {
          parsedEntity,
          fieldsPreview,
          status: this.getUploadRowPreviewStatus(fieldsPreview),
        };
      }),
    );
  }

  private getExistingFieldValue(
    field: keyof PromoDaysBoost,
    existing: StrategyEx | undefined,
  ): PromoDaysBoost[keyof PromoDaysBoost] | undefined {
    if (!existing) return undefined;
    if (field === "activatePrimeDayBoost") {
      return existing.primeDayBoost != null;
    }
    return existing[field];
  }

  private hasFieldChanged(
    field: keyof PromoDaysBoost,
    existingValue: PromoDaysBoost[keyof PromoDaysBoost] | undefined,
    parsedValue: PromoDaysBoost[keyof PromoDaysBoost] | undefined,
  ): boolean {
    if (field === "name" || field === "strategyId") return false;
    return existingValue !== parsedValue;
  }

  private getUploadRowPreviewStatus(fieldsPreview: BulkUploadFieldPreview<PromoDaysBoost>[]): BulkUploadPreviewStatus {
    if (rowHasFieldError(fieldsPreview)) return BulkUploadPreviewStatus.VALUE_ERROR;
    if (rowIsUnchanged(fieldsPreview)) return BulkUploadPreviewStatus.UNCHANGED;
    return BulkUploadPreviewStatus.UPDATE;
  }

  private getStrategyService(campaignType: CampaignType): BaseStrategyService<CampaignType> {
    switch (campaignType) {
      case CampaignType.SP:
        return this.spStrategyService;
      case CampaignType.SD:
        return this.sdStrategyService;
      case CampaignType.SB:
        return this.sbStrategyService;
      default:
        throw new Error(`Unsupported campaign type: ${campaignType}`);
    }
  }
}

type BulkFieldValidator<T> = (entity: Partial<T>) => string | Observable<string | null> | null;

const VALID_BOOSTS = new Set([-50, -25, 0, 25, 50, 100]);

const promoDaysBoostFieldValidators: Partial<Record<keyof PromoDaysBoost, BulkFieldValidator<PromoDaysBoost>>> = {
  strategyId: function (this: PromoBoostBulkUploadHandler, entity) {
    return this.strategyIndex$.pipe(
      take(1),
      map((strategyIndex) => {
        if (entity.strategyId === undefined || Number.isNaN(entity.strategyId))
          return "Strategy ID is not a number or is missing";
        if (!strategyIndex.has(entity.strategyId!)) return "Strategy does not exist";
        if (this.seenStrategyIdsDuringPreview.has(entity.strategyId!)) return "Duplicated strategy update";
        this.seenStrategyIdsDuringPreview.add(entity.strategyId!);
        return null;
      }),
    );
  },
  primeDayBoost: function (entity) {
    if (entity.activatePrimeDayBoost && !VALID_BOOSTS.has(entity.primeDayBoost!)) {
      return "Invalid boost value";
    }
    return null;
  },
  activatePrimeDayBoost: function (this: PromoBoostBulkUploadHandler, entity) {
    return this.strategyIndex$.pipe(
      take(1),
      map((strategyIndex) => {
        const strategy = strategyIndex.get(entity.strategyId!);
        if (!strategy) return null;
        if (strategy.algoMode !== AlgoMode.ACOS_TARGET) {
          return "Boost is not allowed on non ACOS_TARGET strategies";
        }
        if (strategy.dailyBudget && strategy.dailyBudget > 0) {
          return "Boost is not allowed on daily-budgeted strategies";
        }
        return null;
      }),
    );
  },
};
